import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Observable, of, ReplaySubject } from 'rxjs';
import { IsfStateService } from '../../../services';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { FormHelper } from '@helpers/form.helper';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { enumIsfShipmentType } from '@app/data-layer/enums/isf';
import { enumEntityByKey } from '@data-layer/enums/entities';
import { EntityModel } from '@data-layer/models/entities';
import { BondModel, ShipmentModel } from '@data-layer/models/common';
import { IsfEstimatedInfoModel, IsfModel } from '@data-layer/models/isf';
import { CompanyModel } from '@data-layer/models/companies';
import { CompaniesService } from '@services/companies.service';
import { switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { BondRequestRepository } from '@data-layer/repositories';
import { InvoiceModel } from '@data-layer/models/entry-summaries';
import { isCbpAssignedNumberImporter } from '@helpers/importer.helper';

@Component({
  selector: 'desktop-shipment-details-type',
  template: `
    <p>{{ 'LABELS.CHOOSE_TYPE_OF_SHIPMENT' | translate }}</p>
    <p class="text-hint" *ngIf="isAniImporter">
      {{ 'LABELS.FORMAL_ENTRY_REQUIRED_FOR_CBP_ASSIGNED_NUMBER_HINT' | translate }}
    </p>

    <form [formGroup]="formGroup" (ngSubmit)="saveOnButtonClick(true)" autocomplete="off">
      <div class="d-flex justify-content-between">
        <div
          class="card card-step shipment-type"
          [ngClass]="{
            'is-selected': typeControl.value === enumIsfShipmentType.FORMAL,
            'is-full-width': isOnlyFormalTypeAvailable
          }"
        >
          <div class="card-header">
            <app-radio-button
              [tooltipData]="shipmentTypeTooltip | translate"
              [control]="typeControl"
              [id]="'shipment-type-formal'"
              [label]="'FORM_LABEL.FORMAL'"
              [value]="enumIsfShipmentType.FORMAL"
              [name]="'shipment-type'"
            ></app-radio-button>
          </div>
          <div class="card-body px-3" *ngIf="isFormalThresholdHintVisible">
            <span [innerHTML]="'FORM_HELPER.FORMAL_DESKTOP' | translate"></span>
          </div>
        </div>
        <div
          class="card card-step shipment-type"
          *ngIf="isInformalTypeVisible"
          [ngClass]="{ 'is-selected': typeControl.value === enumIsfShipmentType.INFORMAL }"
        >
          <div class="card-header">
            <app-radio-button
              [tooltipData]="shipmentTypeTooltip | translate"
              [control]="typeControl"
              [id]="'shipment-type-informal'"
              [label]="'FORM_LABEL.INFORMAL'"
              [value]="enumIsfShipmentType.INFORMAL"
              [name]="'shipment-type'"
              [disabled]="isAniImporter"
            ></app-radio-button>
          </div>
          <div class="card-body px-3">
            <span [innerHTML]="'FORM_HELPER.INFORMAL_DESKTOP' | translate"></span>
          </div>
        </div>
      </div>

      <div class="mt-3" *ngIf="isFormal">
        <div class="shipment-type-info">
          <p>{{ 'LABELS.CUSTOMS_BOND' | translate }}</p>
          <span [innerHTML]="'INFO.BN.SUB_TITLE' | translate"></span>
        </div>
        <desktop-shipment-details-formal
          [isSideBar]="isSideBar"
          [formGroupControlsOnUpdate]="formGroupControlsOnUpdate"
          [isUpdateOnExistingBondChange]="isUpdateOnExistingBondChange"
          (formValidityUpdated)="formValidityUpdated.emit($event)"
          (bondDataChanged)="bondDataChanged.emit($event)"
          (isPurchaseBondChanged)="isPurchaseBondChanged.emit($event)"
          (arrivalPortChanged)="arrivalPortChanged.emit($event)"
        ></desktop-shipment-details-formal>
      </div>

      <div class="mt-3" *ngIf="!isFormal">
        <div class="shipment-type-info">
          <p>{{ 'INFO.IE.TITLE' | translate }}</p>
          <span>{{ 'INFO.IE.SUB_TITLE' | translate }}</span>
        </div>
        <desktop-shipment-details-informal
          [invoices]="invoices"
          [isSideBar]="isSideBar"
          [isUpdateIsfOnChange]="isUpdateIsfOnChange"
          [formGroupControlsOnUpdate]="formGroupControlsOnUpdate"
          (formUpdated)="onInformalFormUpdate($event)"
          (formValidityUpdated)="formValidityUpdated.emit($event)"
        ></desktop-shipment-details-informal>
      </div>

      <div class="shipment-type-form-buttons" *ngIf="isSideBar">
        <button type="submit" class="btn btn-primary m-auto">
          {{ 'BUTTON.SAVE' | translate }}
        </button>
      </div>
    </form>
  `,
  styleUrls: ['./desktop-shipment-details-type.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DesktopShipmentDetailsTypeComponent implements OnInit, OnDestroy {
  @Input() public isSideBar: boolean;
  @Input() public invoices: InvoiceModel[];
  @Input() public formGroupControlsOnUpdate: 'change' | 'blur' | 'submit' = 'blur';
  @Input() public isUpdateIsfOnChange: boolean = true;
  @Input() public isUpdateOnExistingBondChange: boolean = true;
  @Output() public dataSaved: EventEmitter<void> = new EventEmitter<void>();
  @Output() public formValidityUpdated: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public informalFormUpdated: EventEmitter<IsfEstimatedInfoModel> = new EventEmitter<IsfEstimatedInfoModel>();
  @Output() public typeControlChanged: EventEmitter<enumIsfShipmentType> = new EventEmitter<enumIsfShipmentType>();
  @Output() public bondDataChanged: EventEmitter<BondModel> = new EventEmitter<BondModel>();
  @Output() public isPurchaseBondChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public arrivalPortChanged: EventEmitter<string> = new EventEmitter<string>();

  public formGroup: UntypedFormGroup;
  public typeControl: UntypedFormControl;
  public errors: { [name: string]: IErrorForm } = {
    code: {
      required: 'FORM_ERROR.REQUIRED',
    },
    suretyCode: {
      required: 'FORM_ERROR.REQUIRED',
      wrongSuretyCode: 'FORM_ERROR.WRONG_SURETY_CODE',
    },
    referenceNumber: {
      required: 'FORM_ERROR.REQUIRED',
      wrongReferenceNumber: 'FORM_ERROR.WRONG_REFERENCE_NUMBER',
    },
  };
  public enumIsfShipmentType: typeof enumIsfShipmentType = enumIsfShipmentType;
  public importer: EntityModel;
  public referenceNumber: string;

  public company: CompanyModel;
  public bond: BondModel;
  public shipment: ShipmentModel;

  private temporaryEstimatedInfo: IsfEstimatedInfoModel = null;

  private isf: IsfModel;

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(
    private fh: FormHelper,
    private isfStateService: IsfStateService,
    private companiesService: CompaniesService,
    private fb: UntypedFormBuilder,
    private bondRequestRepository: BondRequestRepository,
  ) {}

  public ngOnInit(): void {
    this.formGroup = this.fb.group({
      type: new UntypedFormControl(enumIsfShipmentType.FORMAL, Validators.required),
    });
    this.typeControl = this.formGroup.get('type') as UntypedFormControl;

    this.isf = this.isfStateService.currentIsf;
    this.referenceNumber = this.isfStateService.currentIsf ? this.isfStateService.currentIsf.referenceNumber : null;

    this.patchShipmentData();

    this.importer = this.isfStateService.getIsfEntityById(enumEntityByKey.IMPORTER);

    if (!this.isSideBar) {
      this.saveOnFormUpdate();
    }

    this.typeControlChanged.emit(this.typeControl.value);
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  public get isFormal(): boolean {
    return this.typeControl.value === enumIsfShipmentType.FORMAL;
  }

  public get isAniImporter(): boolean {
    return isCbpAssignedNumberImporter(this.importer);
  }

  /**
   * #13284: Informal is not a valid choice for an importer with a CBP Assigned Number, so the option is
   * hidden. It stays visible (disabled) while it is the value the filing actually carries, so a legacy or
   * already-submitted Informal filing still shows its real type instead of an empty selection.
   */
  public get isInformalTypeVisible(): boolean {
    return !this.isAniImporter || this.typeControl.value === enumIsfShipmentType.INFORMAL;
  }

  /** Formal spans the whole row when it is the only card rendered (#13284). */
  public get isOnlyFormalTypeAvailable(): boolean {
    return !this.isInformalTypeVisible;
  }

  /** #13346: the $250 threshold is moot when Formal is the only card left, so the copy is dropped there. */
  public get isFormalThresholdHintVisible(): boolean {
    return this.isInformalTypeVisible;
  }

  public get shipmentTypeTooltip(): string {
    return this.isAniImporter ? 'LABELS.FORMAL_ENTRY_REQUIRED_FOR_CBP_ASSIGNED_NUMBER_HINT' : 'TOOLTIP.SHIPMENT_TYPE_TOOLTIP';
  }

  public saveOnButtonClick(needValidate?: boolean): void {
    this.saveData();
  }

  public saveData() {
    this.updateIsfData()
      .pipe(take(1))
      .subscribe(() => {
        this.dataSaved.emit();
      });
  }

  public onInformalFormUpdate(data: IsfEstimatedInfoModel): void {
    if (this.isSideBar) {
      this.temporaryEstimatedInfo = data;
    }
    this.informalFormUpdated.emit(data);
  }

  private patchShipmentData() {
    const shipment: ShipmentModel = this.isfStateService.getShipment();
    this.shipment = shipment;

    this.fh.patchIsfShipmentDetailsFormGroup(this.formGroup, this.isfStateService.currentIsf?.type, this.shipment);
  }

  private updateIsfData(): Observable<any> {
    return this.isfStateService
      .updateIsf(
        {
          ...this.isfStateService.currentIsf,
          importerId: this.isfStateService.currentIsf?.importer?.id,
          type: this.typeControl.value,
          bondInfo: this.typeControl.value === enumIsfShipmentType.INFORMAL ? null : this.isfStateService.currentIsf.bondInfo,
          estimatedInformation:
            this.typeControl.value === enumIsfShipmentType.FORMAL ? null : this.isfStateService.currentIsf.estimatedInformation,
          ...(!!this.temporaryEstimatedInfo && { estimatedInformation: this.temporaryEstimatedInfo }),
        },
        !!this.isSideBar,
      )
      .pipe(
        tap((isf) => {
          this.isfStateService.currentIsf = isf;
        }),
        switchMap(() => {
          const currentBondRequest = this.isfStateService.currentIsf?.bondRequest;
          if (this.typeControl.value === enumIsfShipmentType.INFORMAL && !!currentBondRequest?.id) {
            return this.bondRequestRepository.delete(currentBondRequest.id);
          } else {
            return of(null);
          }
        }),
      );
  }

  private saveOnFormUpdate(): void {
    this.typeControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        switchMap((value) => {
          return this.updateIsfData().pipe(switchMap(() => of(value)));
        }),
      )
      .subscribe((value) => {
        this.typeControlChanged.emit(value);
      });
  }
}
